/* VideoModal · immersive video pop-up
 * Full-screen dark overlay + Vimeo/YouTube/HLS embed + story breakdown.
 * Consumed by the Watch Now and Trailers rails on /stories.
 * iter263 · Backdrop click + Escape close, always-visible X, body
 * scroll lock, and a dark "coming soon" panel when there's no video.
 */
import { useEffect } from 'react';
import { X, ArrowRight, Bookmark, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { resolveImageUrl } from '../ImageUpload';

const embedFor = (url = '') => {
  const u = String(url);
  // Preserve the unlisted-video hash (`vimeo.com/{id}/{hash}`) — Vimeo
  // refuses to embed private videos without `?h=<hash>`.
  const v = u.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([A-Za-z0-9]+))?/);
  if (v) {
    const base = `https://player.vimeo.com/video/${v[1]}`;
    const params = `autoplay=1&title=0&byline=0&portrait=0`;
    return v[2]
      ? `${base}?h=${v[2]}&${params}`
      : `${base}?${params}`;
  }
  const y = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/|youtube\.com\/live\/)([\w-]{6,})/);
  if (y) {
    // iOS Capacitor webview quirks: the standard youtube.com embed
    // trips YouTube's "Video player configuration error / 153" when
    // played inside a TestFlight webview (capacitor:// origin + strict
    // cookies). Fix by (a) switching to youtube-nocookie.com which has
    // broader embed permissions, (b) forcing `playsinline=1` so iOS
    // renders inline instead of the fullscreen app-cover shim, and
    // (c) mute-enabled autoplay so mobile Safari's autoplay policy
    // doesn't reject the initial play call.
    return `https://www.youtube-nocookie.com/embed/${y[1]}?autoplay=1&rel=0&playsinline=1&modestbranding=1&iv_load_policy=3`;
  }
  return u; // fallback: raw mp4 or hls url in iframe/video tag below
};

const VideoModal = ({ story, onClose }) => {
  // Escape closes + lock the page scroll so closing drops the user
  // right back where they were in the feed.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  if (!story) return null;
  const src = embedFor(story.video_url || story.trailer_url || story.vimeo_url || '');
  const isRaw = src && /\.(mp4|m3u8|webm)(\?|$)/i.test(src);
  return (
    <div
      className="fixed inset-0 z-[400] bg-black/95 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 overflow-y-auto"
      data-testid="story-video-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      {/* X pinned to the viewport — always reachable even when the
          breakdown content scrolls on mobile. */}
      <button
        type="button"
        onClick={onClose}
        className="fixed top-4 right-4 z-[410] w-11 h-11 rounded-full bg-white/15 ring-1 ring-white/30 text-white hover:bg-white/30 flex items-center justify-center shadow-xl"
        data-testid="story-video-modal-close"
        aria-label="Close"
      >
        <X className="w-5 h-5" />
      </button>
      <div className="w-full max-w-5xl my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="aspect-video w-full rounded-2xl overflow-hidden bg-black ring-1 ring-white/10">
          {src ? (
            isRaw
              ? <video src={src} controls autoPlay className="w-full h-full" poster={resolveImageUrl(story.thumbnail)} />
              : <iframe src={src} title={story.name} className="w-full h-full" allow="autoplay; encrypted-media; picture-in-picture; fullscreen; accelerometer; gyroscope" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" />
          ) : (
            /* No video attached — never show a giant bare photo (reads
               like a broken white screen). Dark hype panel instead. */
            <div className="relative w-full h-full" data-testid="story-video-modal-coming-soon">
              {(story.thumbnail || story.cover_image) && (
                <img src={resolveImageUrl(story.thumbnail || story.cover_image)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/40" />
              <div className="relative w-full h-full flex flex-col items-center justify-center text-center px-6">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 ring-1 ring-white/25 text-white text-[10px] font-black uppercase tracking-[0.28em]">
                  <Clock className="w-3 h-3" /> Coming soon
                </span>
                <p className="mt-4 font-display text-2xl sm:text-3xl text-white uppercase leading-tight">{story.name}</p>
                <p className="mt-2 text-white/60 text-sm">The trailer drops here first. Check back soon.</p>
              </div>
            </div>
          )}
        </div>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <p className="text-[10px] uppercase tracking-widest text-blue-300 font-bold">{story.category || 'FLYT Original'}</p>
            <h2 className="font-display text-2xl sm:text-3xl uppercase leading-tight text-white mt-1">{story.name}</h2>
            {(story.description || story.excerpt) && (
              <p className="mt-3 text-white/70 text-sm leading-relaxed">{story.description || story.excerpt}</p>
            )}
            <div className="mt-4 flex items-center gap-2">
              {story.athlete_slug && (
                <Link to={`/network/${story.athlete_slug}`} onClick={onClose} className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-white text-black text-[10px] font-black uppercase tracking-widest hover:bg-blue-200" data-testid="story-video-modal-athlete">
                  View athlete <ArrowRight className="w-3 h-3" />
                </Link>
              )}
              <button type="button" className="inline-flex items-center gap-1 px-4 py-2 rounded-full ring-1 ring-white/20 bg-white/5 text-white text-[10px] font-bold uppercase tracking-widest hover:bg-white/15" aria-label="Save">
                <Bookmark className="w-3 h-3" /> Save
              </button>
            </div>
          </div>
          <aside className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-4 space-y-2 text-sm text-white/70">
            {story.athlete_name && <p><span className="text-white/40 text-[10px] uppercase tracking-widest block">Athlete</span>{story.athlete_name}</p>}
            {story.team && <p><span className="text-white/40 text-[10px] uppercase tracking-widest block">Team</span>{story.team}</p>}
            {(story.duration_minutes || story.estimated_duration) && <p><span className="text-white/40 text-[10px] uppercase tracking-widest block">Runtime</span>{story.duration_minutes || story.estimated_duration} min</p>}
            {story.publish_date && <p><span className="text-white/40 text-[10px] uppercase tracking-widest block">Released</span>{new Date(story.publish_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default VideoModal;
